// The app's date fields (issue 823): what every @lumi/ui DateField of an app
// falls back to, and the bounds a kind of date brings with it, so every form
// asking for one behaves the same.

import { addMonthsIso, configureDateFields, todayIso } from '@lumi/ui';
import { t } from '../i18n';
import { uiLocale } from './uiLocale';

/** vue-i18n's global composer, as far as this needs it: its locale is a ref in composition mode, a string in legacy mode. */
export interface DateFieldsI18n {
  locale: string | { value: string };
}

/**
 * Every DateField of the app formats in its locale and has the month/year calendar
 * and the typing hint in its words — without each of the ~80 call sites passing
 * them. Call once in main.ts, after `registerAppKitI18n(i18n.global)` (the words
 * are app-kit's `common.datePicker.*`).
 */
export function registerDateFields(i18n: DateFieldsI18n): void {
  configureDateFields({
    locale: () => uiLocale(typeof i18n.locale === 'string' ? i18n.locale : i18n.locale.value),
    labels: () => ({
      open: t('common.datePicker.open'),
      previousMonth: t('common.datePicker.previousMonth'),
      nextMonth: t('common.datePicker.nextMonth'),
      month: t('common.datePicker.month'),
      year: t('common.datePicker.year'),
      clear: t('common.datePicker.clear'),
      dayToken: t('common.datePicker.dayToken'),
      monthToken: t('common.datePicker.monthToken'),
      yearToken: t('common.datePicker.yearToken'),
    }),
  });
}

/** Nobody whose record the suite keeps is younger than this. */
export const MIN_BIRTH_DATE_AGE = 14;

/**
 * A birth date: at least 14 years back. The calendar opens there, later days are
 * not offered, and a typed `1.4.90` is 1990, never 2090.
 * Bind it on a DateField: `:max="birthDate.max" :anchor="birthDate.anchor"`.
 */
export function birthDateField(now: Date = new Date()): { max: string; anchor: string } {
  const max = addMonthsIso(todayIso(now), -12 * MIN_BIRTH_DATE_AGE)!;
  return { max, anchor: max };
}
